import { useState } from "react";
import { Button } from "@/components/ui/button";
import { SMB_ENRICHMENT_STEPS, smbEnrichmentFlagSchema, type SmbEnrichmentFlag } from "@shared/smb-enrichment";
import type { SmbAdminStep } from "@shared/smb-admin";

const labels = ["Provision brand", "Apply client answers", "Fill empty fields",
  "Create services and promotions", "Generate 10, select 3", "Confidence"];
const reviewLabels: Record<SmbEnrichmentFlag, string> = {
  missing_primary_logo: "No primary logo was supplied at acceptance.",
  missing_services: "A service product is missing.",
  persona_configuration: "Persona provider configuration is unavailable.",
  persona_failures: "Starter personas need review or repair.",
  low_confidence: "Confidence is below 0.6.",
  uncertain_request: "A paid request has an uncertain outcome. Inspect or repair it before resuming. Approval does not resubmit it.",
  research_failed: "Brand research is unavailable or did not finish.",
  angle_failures: "Selected personas do not yet have 2-3 completed angles.",
};
export function EnrichmentControls({ step, orderId, busy, onRun, compact = false, onApproved }: {
  step: SmbAdminStep; orderId: string; busy: boolean;
  onRun(key: string, dryRun: boolean): void;
  compact?: boolean; onApproved?: () => void;
}) {
  const state = step.description ?? step.state?.result ?? {};
  const recoveryLabel = state.personaRecoveryKind === "saved_results" ? "Fix saved persona results" : "Resume interrupted personas";
  const personasFinished = typeof state.completedCount === "number" && state.completedCount >= 10;
  const anglesFinished = state.narrativeAngleStatus === "completed";
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState("");
  const flags = Array.isArray(state.reviewFlags) ? state.reviewFlags.flatMap(value => {
    const parsed = smbEnrichmentFlagSchema.safeParse(value);
    return parsed.success ? [parsed.data] : [];
  }) : [];
  const held = state.reviewRequired === true;
  const next = typeof state.nextStep === "string" ? state.nextStep : "enrichment";
  const completed = Array.isArray(state.steps) ? state.steps : [];
  const completedCount = typeof state.completedCount === "number" && Number.isFinite(state.completedCount) ? state.completedCount : null;
  const profileCompletedCount = typeof state.profileCompletedCount === "number" && Number.isFinite(state.profileCompletedCount)
    ? state.profileCompletedCount : completedCount;
  const generatedCount = typeof state.generatedCount === "number" && Number.isFinite(state.generatedCount) ? state.generatedCount : null;
  const selectedCount = typeof state.selectedCount === "number" && Number.isFinite(state.selectedCount) ? state.selectedCount : null;
  const jobCounts = state.personaJobCounts && typeof state.personaJobCounts === "object"
    ? state.personaJobCounts as Record<string, unknown> : {};
  const count = (key: string) => typeof jobCounts[key] === "number" && Number.isFinite(jobCounts[key])
    ? Math.max(0, jobCounts[key] as number) : 0;
  const waiting = count("waiting"), running = count("running"), finished = count("completed");
  const failed = count("failed"), interrupted = count("interrupted"), discarded = count("discarded");
  const recoveryAvailable = state.personaRecoveryAvailable === true;
  const otherHoldFlags = flags.some(flag => flag !== "persona_failures") || (held && flags.length === 0);
  const personaWorkRunning = running > 0 || state.personaBatchStatus === "running";
  const canResumePersonas = recoveryAvailable && !personaWorkRunning && !otherHoldFlags;
  const hasPersonaProgress = (profileCompletedCount ?? 0) > 0 || (generatedCount ?? 0) > 0 ||
    completedCount !== null || next === "personas" || Object.keys(jobCounts).length > 0;
  const approve = async () => {
    setApproving(true); setError("");
    try {
      const { apiRequest } = await import("@/lib/queryClient");
      await apiRequest("POST", `/api/admin/smb/orders/${encodeURIComponent(orderId)}/enrichment/approve`, { flags });
      if (compact) onApproved?.();
      else onRun(next, false);
    } catch { setError("Approval could not be saved. Refresh the order and review its current hold."); }
    finally { setApproving(false); }
  };
  return <div className="mb-4 space-y-3" data-testid="smb-enrichment-controls">
    <p className="text-sm">{personasFinished
      ? "The 10 persona builds are complete. Select 3 for this package, then build 2–3 narrative angles for each selected persona. Keep all other personas."
      : "Generate the normal 10 personas. Freeze 3 completed personas for this package and retain all other outputs."}</p>
    <div className="space-y-2 text-xs text-muted-foreground">
      <p>
        Persona profiles completed: {profileCompletedCount ?? 0} / 10. Selected: {selectedCount ?? 0} / 3.
      </p>
      {hasPersonaProgress && <div className="space-y-1.5 rounded-lg border bg-muted/25 p-3" role="status" aria-label="Persona profile progress">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium text-foreground">Persona work</span>
          {generatedCount !== null && <span>Persona jobs created: {generatedCount}</span>}
        </div>
        <progress
          className="block h-2 w-full overflow-hidden rounded-full accent-primary"
          max={10}
          value={profileCompletedCount === null ? undefined : Math.min(10, Math.max(0, profileCompletedCount))}
          aria-label="Persona profiles completed"
          aria-valuetext={profileCompletedCount === null ? "Persona profile completion has not been reported" : `${profileCompletedCount} of 10 persona profiles completed`}
        />
        <p className="font-medium text-foreground">Persona builds finished: {completedCount ?? finished}</p>
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3" aria-label="Persona job counts">
          <li>Waiting: {waiting}</li><li>Running: {running}</li><li>Finished: {finished}</li>
          <li>Failed: {failed}</li><li>Interrupted: {interrupted}</li><li>Discarded: {discarded}</li>
        </ul>
        {interrupted > 0 && <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 font-medium text-foreground">
          Persona finishing work interrupted. {profileCompletedCount ?? 0} completed profiles are preserved.
        </p>}
        {typeof state.savedAvatarRepairCount === "number" && state.savedAvatarRepairCount > 0 &&
          <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 font-medium text-foreground">
            {state.savedAvatarRepairCount} character images were saved, but marked failed because billing cost metadata was unavailable.
            {" "}Use Fix saved persona results to restore their saved status without generating images again. Recorded cost remains unknown.
          </p>}
        <p>{personasFinished
          ? "Persona builds are complete, including their character images. Narrative-angle progress is shown separately below."
          : "Profile completion does not mean paid angle or avatar work is complete."}</p>
      </div>}
      {hasPersonaProgress && <div className="space-y-1.5 rounded-lg border bg-muted/25 p-3" aria-label="Narrative angle progress">
        <p className="font-medium text-foreground">Next: narrative angles</p>
        <p>Selected personas: {selectedCount ?? 0} / 3. Target: 2–3 angles per selected persona.</p>
        <p>Angles generated for this order: {typeof state.narrativeAngleCount === "number" ? state.narrativeAngleCount : "Not yet reported"}.
          {" "}{anglesFinished ? "Complete." : state.narrativeAngleStatus === "needs_review" ? "Needs review." :
            state.narrativeAngleStatus === "in_progress" ? "In progress." : personasFinished ? "Not started." : "Starts after persona builds finish."}</p>
        {personasFinished && !anglesFinished && <p>The next action uses the completed personas. It does not build them again.</p>}
      </div>}
    </div>
    {held && <div className="rounded-lg border bg-muted/50 p-3 text-sm text-foreground" role="status">
      <p className="font-medium">Review hold</p>
      <ul className="my-2 list-inside list-disc">{flags.map(flag => <li key={flag}>{reviewLabels[flag]}</li>)}</ul>
      {!(recoveryAvailable && flags.length > 0 && flags.every(flag => flag === "persona_failures")) &&
        <Button size="sm" variant="outline" onClick={() => void approve()} disabled={busy || approving || !flags.length}>{compact ? "Approve reviewed hold" : "Approve and continue"}</Button>}
      {recoveryAvailable && flags.length > 0 && flags.every(flag => flag === "persona_failures") &&
        <div className="space-y-1">
          {!compact && <Button size="sm" variant="outline" onClick={() => onRun("personas", false)} disabled={busy || approving || !canResumePersonas}>{recoveryLabel}</Button>}
          {compact && <p className="text-xs text-muted-foreground">Use {recoveryLabel} below to review and confirm recovery.</p>}
          {!canResumePersonas && <p className="text-xs text-muted-foreground">Review other hold requirements or refresh status before resuming.</p>}
        </div>}
    </div>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    <div className="divide-y rounded-lg border">
      {SMB_ENRICHMENT_STEPS.map((key, index) => {
        const done = completed.some(value => value && typeof value === "object" && !Array.isArray(value) &&
          value.step === key && value.completed === true);
        return <div key={key} className="flex flex-wrap items-center justify-between gap-2 p-3 text-xs">
          <span>{key === "personas" && personasFinished ? "Select 3 personas and build narrative angles" : labels[index]}{done ? " (completed)" : next === key ? " (next)" : ""}</span>
          {!compact && <span className="flex gap-2">
            <Button size="sm" variant="outline" disabled={busy || approving || held || (next !== key && !done)}
              onClick={() => onRun(key, false)}>Run / resume</Button>
            <Button size="sm" variant="ghost" disabled={busy || approving} onClick={() => onRun(key, true)}>Dry run</Button>
          </span>}
        </div>;
      })}
    </div>
  </div>;
}
